import React, { useMemo } from 'react'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Descriptions, Space, Spin, Table, Tag } from 'antd'
import HotelCoverageSearch from '../HotelCoverageSearch'
import HotelEditor from '../HotelEditor'
import HotelRegionForm from '../HotelRegionForm'
import { ErrorBox, RegionLink, supplierColumns } from '../CoverageCommon'
import container from './container'

const HotelResult = props => {
    const { result, hotelId, perms, onCreate, onEdit, onDelete, onAddRegion, onRemoveRegion } = props

    const regionColumns = useMemo(() => {
        return [
            {
                key: 'region_id',
                dataIndex: 'region_id',
                title: 'Region ID',
                render: id => <RegionLink id={id} />
            },
            {
                key: 'region_name_full',
                title: 'Region',
                render: region => region.region_name_full || region.region_name
            },
            { key: 'region_type', dataIndex: 'region_type', title: 'Type' },
            { key: 'supplier_code', dataIndex: 'supplier_code', title: 'Source' },
            { key: 'is_active', dataIndex: 'is_active', title: 'Active', width: 90, render: v => (v ? 'yes' : 'no') },
            perms.edit
                ? {
                      key: 'action',
                      title: 'Action',
                      width: 110,
                      render: region => (
                          <Button danger size="small" onClick={() => onRemoveRegion(result, region)}>
                              Remove
                          </Button>
                      )
                  }
                : null
        ].filter(i => i)
    }, [perms.edit, result, onRemoveRegion])

    if (!result.success) {
        if (result.status === 404) {
            return (
                <Alert
                    type="warning"
                    showIcon
                    message={result.message || `Hotel ${hotelId} not found`}
                    description="There is no hotel_mapping_provider row for this hotel_id, so search cannot return it."
                    action={
                        perms.create ? (
                            <Button size="small" type="primary" onClick={() => onCreate(hotelId)}>
                                Create hotel
                            </Button>
                        ) : null
                    }
                />
            )
        }
        return <ErrorBox result={result} />
    }

    const rows = [
        ...result.suppliers.map(s => ({ ...s, status: 'mapped' })),
        ...result.missing.map(m => ({ ...m, status: 'missing' }))
    ]
    const info = result.static || {}

    return (
        <>
            <Card
                title={info.name ? `Hotel ${result.hotel_id} · ${info.name}` : `Hotel ${result.hotel_id}`}
                extra={
                    <Space>
                        <Tag color={result.mapped ? 'green' : 'red'}>
                            {result.mapped} / {result.total} catalogs mapped
                        </Tag>
                        {perms.edit ? (
                            <Button icon={<EditOutlined />} onClick={() => onEdit(result)}>
                                Edit
                            </Button>
                        ) : null}
                        {perms.delete ? (
                            <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(result)}>
                                Delete
                            </Button>
                        ) : null}
                    </Space>
                }>
                <Descriptions size="small" column={4}>
                    {info.city ? <Descriptions.Item label="City">{info.city}</Descriptions.Item> : null}
                    {info.category ? <Descriptions.Item label="Category">{info.category}</Descriptions.Item> : null}
                    <Descriptions.Item label="Country">{result.country_code || info.country || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Timezone">{result.timezone || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Rank">{result.rank}</Descriptions.Item>
                    <Descriptions.Item label="Active">{result.is_active ? 'yes' : 'no'}</Descriptions.Item>
                </Descriptions>
                <Table
                    bordered
                    size="small"
                    rowKey="column"
                    dataSource={rows}
                    columns={supplierColumns}
                    pagination={false}
                />
            </Card>
            <br />
            <Card
                title={`Regions (${result.regions.length})`}
                extra={
                    perms.edit ? (
                        <Button icon={<PlusOutlined />} onClick={() => onAddRegion(result)}>
                            Add region
                        </Button>
                    ) : null
                }>
                <Table
                    bordered
                    size="small"
                    rowKey={r => `${r.supplier_code}-${r.region_id}`}
                    dataSource={result.regions}
                    columns={regionColumns}
                    pagination={false}
                />
            </Card>
        </>
    )
}

const HotelCoverage = props => {
    const {
        hotelCoverage,
        loading,
        query,
        perms,
        handleCreate,
        handleEdit,
        handleDelete,
        handleAddRegion,
        handleRemoveRegion
    } = props

    return (
        <div>
            <HotelCoverageSearch canCreate={perms.create} onCreate={handleCreate} />
            <br />
            {hotelCoverage ? (
                <Spin spinning={loading}>
                    <HotelResult
                        result={hotelCoverage}
                        hotelId={query && query.hotel_id}
                        perms={perms}
                        onCreate={handleCreate}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onAddRegion={handleAddRegion}
                        onRemoveRegion={handleRemoveRegion}
                    />
                </Spin>
            ) : loading ? (
                <Card size="small" loading />
            ) : null}
            <HotelEditor />
            <HotelRegionForm />
        </div>
    )
}

export default container(HotelCoverage)
